import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as XLSX from 'xlsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

import Box from '../components/Box';
import LoadingProgress from '../components/LoadingProgress';
import ErrorMessage from '../components/ErrorMessage';
import { fetchRentals } from '../actions/rentals';
import { fetchCustomersName } from '../actions/customers';
import '../styles/DashboardView.css';

// ─── helpers ────────────────────────────────────────────────────────────────

function localIso(d) {
  if (!(d instanceof Date) || isNaN(d)) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function rentalDays(rental) {
  const start = new Date(rental.StartDate);
  const finish = new Date(rental.FinishDate);
  if (isNaN(start) || isNaN(finish)) return null;
  const diff = finish - start;
  if (diff <= 0) return 1;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const BUCKET_LABELS = ['1', '2', '3', '4', '5', '6', '7', '8-14', '15-30', '31+'];
const BUCKET_COLORS = [
  '#2f6fdb', '#3b82f6', '#60a5fa', '#93c5fd',
  '#fbbf24', '#f59e0b', '#10b981',
  '#6366f1', '#8b5cf6', '#ef4444',
];

function daysToBucket(days) {
  if (days === null || days === undefined) return null;
  if (days <= 7) return String(days);
  if (days <= 14) return '8-14';
  if (days <= 30) return '15-30';
  return '31+';
}

function buildDistribution(rentals) {
  const counts = {};
  BUCKET_LABELS.forEach(l => { counts[l] = 0; });

  rentals.forEach(r => {
    const d = rentalDays(r);
    if (d === null) return;
    const bucket = daysToBucket(d);
    if (bucket && counts[bucket] !== undefined) counts[bucket]++;
  });

  const total = rentals.length;
  return BUCKET_LABELS.map((label, i) => ({
    label,
    count: counts[label] || 0,
    pct: total > 0 ? ((counts[label] || 0) / total) * 100 : 0,
    color: BUCKET_COLORS[i % BUCKET_COLORS.length],
  }));
}

// ─── view ────────────────────────────────────────────────────────────────────

class DashboardRentalView extends React.Component {
  constructor(props) {
    super(props);

    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    this.state = {
      // committed filter values
      startDate: localIso(monthStart),
      endDate: localIso(today),
      // temp (pending) values while user edits
      tempStartDate: localIso(monthStart),
      tempEndDate: localIso(today),
      // client-side customer filter (applied instantly, no reload needed)
      selectedCustomerId: '',
      loading: false,
      fetchError: null,
      showFilters: false,
    };

    this.handleLoad = this.handleLoad.bind(this);
    this.handleExcel = this.handleExcel.bind(this);
  }

  componentDidMount() {
    this.props.fetchCustomersName();
    this.handleLoad();
  }

  handleLoad() {
    const { tempStartDate, tempEndDate } = this.state;
    this.setState({
      startDate: tempStartDate,
      endDate: tempEndDate,
      loading: true,
      fetchError: null,
    });

    this.props
      .fetchRentals(undefined, undefined, tempStartDate, tempEndDate)
      .then(() => this.setState({ loading: false }))
      .catch(err => this.setState({ loading: false, fetchError: String(err) }));
  }

  handleExcel() {
    const { startDate, endDate, selectedCustomerId } = this.state;
    const { customers } = this.props;
    const filtered = this.getFilteredRentals();
    if (filtered.length === 0) return;

    // Helper to resolve customer name from a rental
    const resolveCustomerName = (r) => {
      const cObj = r.Customer && typeof r.Customer === 'object' ? r.Customer : null;
      if (cObj && (cObj.Fullname || cObj.Name)) return cObj.Fullname || cObj.Name;
      const cId = cObj ? String(cObj._id || cObj.id || '') : String(r.Customer || '');
      if (!cId) return '—';
      if (Array.isArray(customers)) {
        const found = customers.find(c => String(c._id || c.id) === cId);
        if (found) return found.Fullname || found.Name || cId;
      }
      return cId;
    };

    // Build per-customer KPI rows
    const customerMap = {};
    filtered.forEach(r => {
      const cObj = r.Customer && typeof r.Customer === 'object' ? r.Customer : null;
      const cId = cObj ? String(cObj._id || cObj.id || '') : String(r.Customer || '');
      const key = cId || '__unknown__';
      if (!customerMap[key]) {
        customerMap[key] = { name: resolveCustomerName(r), all: [], completed: [], canceled: [] };
      }
      customerMap[key].all.push(r);
      const status = (r.Status || '').toUpperCase();
      if (status === 'COMPLETE') customerMap[key].completed.push(r);
      if (status === 'CANCELED' || status === 'CANCELLED') customerMap[key].canceled.push(r);
    });

    const kpiRows = Object.values(customerMap)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => {
        const total = c.all.length;
        const completedCount = c.completed.length;
        const canceledCount = c.canceled.length;
        const daysArr = c.completed.map(r => rentalDays(r)).filter(d => d !== null);
        const avgDays = daysArr.length > 0
          ? (daysArr.reduce((s, d) => s + d, 0) / daysArr.length).toFixed(1)
          : '—';
        const dist = buildDistribution(c.completed);
        const topBucket = dist.reduce((a, b) => (b.count > a.count ? b : a), dist[0]);
        const oneDayPct = completedCount > 0
          ? ((dist.find(d => d.label === '1')?.count || 0) / completedCount * 100).toFixed(1) + '%'
          : '—';
        return {
          'Cliente': c.name,
          'Total alquileres': total,
          'Completados': completedCount,
          '% Completados': total > 0 ? (completedCount / total * 100).toFixed(1) + '%' : '—',
          'Abandonados en carrito': canceledCount,
          '% Abandonados': total > 0 ? (canceledCount / total * 100).toFixed(1) + '%' : '—',
          'Duración media (días)': avgDays,
          'Duración más común': completedCount > 0 ? topBucket?.label + 'd' : '—',
          '% alquileres 1 día': oneDayPct,
        };
      });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(kpiRows);
    XLSX.utils.book_append_sheet(wb, ws, 'KPIs por cliente');

    // Distribution sheet (completed)
    const distCompleted = buildDistribution(filtered.filter(r => (r.Status || '').toUpperCase() === 'COMPLETE'));
    const ws2 = XLSX.utils.json_to_sheet(distCompleted.map(d => ({
      'Duración (días)': d.label,
      'Completados': d.count,
      '% del total completados': d.pct.toFixed(1) + '%',
    })));
    XLSX.utils.book_append_sheet(wb, ws2, 'Distribución completados');

    // Distribution sheet (canceled)
    const distCanceled = buildDistribution(filtered.filter(r => ['CANCELED', 'CANCELLED'].includes((r.Status || '').toUpperCase())));
    const ws3 = XLSX.utils.json_to_sheet(distCanceled.map(d => ({
      'Duración (días)': d.label,
      'Abandonados': d.count,
      '% del total abandonados': d.pct.toFixed(1) + '%',
    })));
    XLSX.utils.book_append_sheet(wb, ws3, 'Distribución abandonados');

    const fileName = `kpis_alquileres_${startDate}_${endDate}${selectedCustomerId ? '_cliente' : ''}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  // apply customer filter client-side
  getFilteredRentals() {
    const { rentals } = this.props;
    const { selectedCustomerId } = this.state;
    if (!Array.isArray(rentals)) return [];
    if (!selectedCustomerId) return rentals;
    return rentals.filter(r => {
      const cid = r.Customer && (r.Customer._id || r.Customer);
      return String(cid) === String(selectedCustomerId);
    });
  }

  render() {
    const {
      tempStartDate,
      tempEndDate,
      selectedCustomerId,
      loading,
      fetchError,
      startDate,
      endDate,
      showFilters,
    } = this.state;
    const { error, customers, role } = this.props;

    const rentals = this.getFilteredRentals();
    const totalRentals = rentals.length;

    // Status breakdown
    const byStatus = rentals.reduce((acc, r) => {
      const s = (r.Status || 'UNKNOWN').toUpperCase();
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    const completedRentals = rentals.filter(r => (r.Status || '').toUpperCase() === 'COMPLETE');
    const completedCount = completedRentals.length;
    const canceledCount = byStatus['CANCELED'] || byStatus['CANCELLED'] || 0;

    // Duration KPIs — COMPLETE only
    const canceledRentals = rentals.filter(r => ['CANCELED', 'CANCELLED'].includes((r.Status || '').toUpperCase()));
    const distCanceled = buildDistribution(canceledRentals);
    const dist = buildDistribution(completedRentals);
    const daysArr = completedRentals.map(r => rentalDays(r)).filter(d => d !== null);
    const avgDays = daysArr.length > 0
      ? (daysArr.reduce((s, d) => s + d, 0) / daysArr.length).toFixed(1)
      : '—';

    // KPI: most common bucket (COMPLETE)
    const topBucket = dist.reduce((a, b) => (b.count > a.count ? b : a), dist[0]);

    // KPI: % of 1-day rentals (COMPLETE)
    const oneDayPct = completedCount > 0
      ? ((dist.find(d => d.label === '1')?.count || 0) / completedCount * 100).toFixed(1)
      : '—';

    // Cancel rate
    const cancelPct = totalRentals > 0
      ? (canceledCount / totalRentals * 100).toFixed(1)
      : '—';

    // Build customer list from rentals actually present in the fetched period
    const allFetchedRentals = Array.isArray(this.props.rentals) ? this.props.rentals : [];
    const seenCustomerIds = new Set();
    const customerOptions = [];
    allFetchedRentals.forEach(r => {
      const cObj = r.Customer && typeof r.Customer === 'object' ? r.Customer : null;
      const cId = cObj ? String(cObj._id || cObj.id || '') : String(r.Customer || '');
      if (!cId || seenCustomerIds.has(cId)) return;
      seenCustomerIds.add(cId);
      const name = (cObj && (cObj.Fullname || cObj.Name)) ||
        (() => {
          if (!Array.isArray(customers)) return cId;
          const found = customers.find(c => String(c._id || c.id) === cId);
          return found ? (found.Fullname || found.Name || cId) : cId;
        })();
      customerOptions.push({ id: cId, name });
    });
    customerOptions.sort((a, b) => a.name.localeCompare(b.name));

    const displayCustomerName = (() => {
      if (!selectedCustomerId) return 'Todos los clientes';
      const opt = customerOptions.find(c => c.id === selectedCustomerId);
      return opt ? opt.name : selectedCustomerId;
    })();

    const errMsg = fetchError || error;

    return (
      <div className="dashboard-view">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="dashboard-header mb-3">
          <div className="dashboard-title-section">
            <div className="dashboard-title">
              <i className="fas fa-calendar-check" style={{ color: '#2f6fdb' }}></i>
              Dashboard Reservas
            </div>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              {startDate} → {endDate}{role === 'ADMIN' ? ` · ${displayCustomerName}` : ''}
            </span>
          </div>
          <div className="dashboard-actions">
            {role === 'ADMIN' && (
              <select
                className="dashboard-filter-input"
                value={selectedCustomerId}
                onChange={e => this.setState({ selectedCustomerId: e.target.value })}
                style={{ minWidth: 200 }}
              >
                <option value="">Todos los clientes</option>
                {customerOptions.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="dashboard-filter-input toggle-filters"
              onClick={() => this.setState({ showFilters: !showFilters })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              <i className="fas fa-sliders-h" style={{ marginRight: 6 }}></i>
              {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            </button>
            <button
              className="btn bg-vw-excel text-white"
              onClick={this.handleExcel}
              disabled={totalRentals === 0}
              title="Descargar Excel"
              style={{ boxShadow: 'none' }}
            >
              <i className="fas fa-file-excel mr-1"></i>Exportar Excel
            </button>
          </div>
        </div>

        {/* ── Filters (collapsible) ───────────────────────────────────── */}
        {showFilters && (
          <div className="dashboard-header mb-3" style={{ justifyContent: 'center' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                  Fecha inicio
                </label>
                <input
                  type="date"
                  className="dashboard-filter-input"
                  value={tempStartDate}
                  onChange={e => this.setState({ tempStartDate: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                  Fecha fin
                </label>
                <input
                  type="date"
                  className="dashboard-filter-input"
                  value={tempEndDate}
                  onChange={e => this.setState({ tempEndDate: e.target.value })}
                />
              </div>
              <button
                className="btn btn-vw ml-2"
                onClick={this.handleLoad}
                disabled={loading}
                style={{ boxShadow: 'none' }}
              >
                <i className="fas fa-sync-alt mr-1"></i>
                {loading ? 'Cargando…' : 'Cargar datos'}
              </button>
            </div>
          </div>
        )}

        {/* ── Loading / Error ──────────────────────────────────────────── */}
        {loading && <LoadingProgress />}
        {!loading && errMsg && <ErrorMessage message={errMsg} />}

        {!loading && (
          <>
            {/* ── KPI cards ───────────────────────────────────────────────── */}
            <div className="dashboard-kpi-wrapper mb-3">
              <div className="dashboard-kpi-row kpi-centered">
                {/* Total */}
                <div className="dashboard-kpi-card" style={{ '--accent': '#2f6fdb' }}>
                  <div className="kpi-icon" style={{ background: '#eff6ff', color: '#2f6fdb' }}>
                    <i className="fas fa-box"></i>
                  </div>
                  <div className="kpi-content">
                    <div className="kpi-label">Documentos</div>
                    <div className="kpi-value">{totalRentals.toLocaleString()}</div>
                  </div>
                </div>

                {/* Completed */}
                <div className="dashboard-kpi-card" style={{ '--accent': '#10b981' }}>
                  <div className="kpi-icon" style={{ background: '#ecfdf5', color: '#10b981' }}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div className="kpi-content">
                    <div className="kpi-label">Completados</div>
                    <div className="kpi-value">{completedCount.toLocaleString()}</div>
                    {totalRentals > 0 && <div style={{ fontSize: 11, color: '#10b981', marginTop: 3 }}>{(completedCount / totalRentals * 100).toFixed(1)}% del total</div>}
                  </div>
                </div>

                {/* Canceled */}
                <div className="dashboard-kpi-card" style={{ '--accent': '#ef4444' }}>
                  <div className="kpi-icon" style={{ background: '#fef2f2', color: '#ef4444' }}>
                    <i className="fas fa-times-circle"></i>
                  </div>
                  <div className="kpi-content">
                    <div className="kpi-label">Abandonados en carrito</div>
                    <div className="kpi-value">{canceledCount.toLocaleString()}</div>
                    {cancelPct !== '—' && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{cancelPct}% del total</div>}
                  </div>
                </div>

                {/* Avg duration (COMPLETE only) */}
                <div className="dashboard-kpi-card" style={{ '--accent': '#f59e0b' }}>
                  <div className="kpi-icon" style={{ background: '#fffbeb', color: '#f59e0b' }}>
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="kpi-content">
                    <div className="kpi-label">Duración media</div>
                    <div className="kpi-value">{avgDays !== '—' ? `${avgDays}d` : '—'}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>solo completados</div>
                  </div>
                </div>

                {/* Most common bucket (COMPLETE only) */}
                <div className="dashboard-kpi-card" style={{ '--accent': '#6366f1' }}>
                  <div className="kpi-icon" style={{ background: '#eef2ff', color: '#6366f1' }}>
                    <i className="fas fa-star"></i>
                  </div>
                  <div className="kpi-content">
                    <div className="kpi-label">Duración más común</div>
                    <div className="kpi-value">
                      {completedCount > 0 ? `${topBucket?.label} día${topBucket?.label === '1' ? '' : 's'}` : '—'}
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>solo completados</div>
                  </div>
                </div>

                {/* % 1-day (COMPLETE only) */}
                <div className="dashboard-kpi-card" style={{ '--accent': '#8b5cf6' }}>
                  <div className="kpi-icon" style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
                    <i className="fas fa-percentage"></i>
                  </div>
                  <div className="kpi-content">
                    <div className="kpi-label">% de 1 día</div>
                    <div className="kpi-value">{oneDayPct !== '—' ? `${oneDayPct}%` : '—'}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>solo completados</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Distribution chart ──────────────────────────────────────── */}
            <div className="dashboard-card-section mb-3">
              <div className="card-header-custom">
                <i className="fas fa-chart-bar mr-2" style={{ color: '#2f6fdb' }}></i>
                Distribución por duración de alquiler <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(alquileres completados)</span>
              </div>
              <div className="card-body-custom">
                {completedCount === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0', fontSize: 14 }}>
                    <i className="fas fa-inbox" style={{ fontSize: 28, display: 'block', marginBottom: 8 }}></i>
                    No hay alquileres completados para el periodo seleccionado.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={dist} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        tickFormatter={v => `${v}d`}
                      />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip
                        formatter={(value, name, props) => [
                          `${value} (${props.payload.pct.toFixed(1)}%)`,
                          'Alquileres',
                        ]}
                        labelFormatter={l => `Duración: ${l} día${l === '1' ? '' : 's'}`}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {dist.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* ── Breakdown table ─────────────────────────────────────────── */}
            {completedCount > 0 && (
              <div className="dashboard-card-section mb-3">
                <div className="card-header-custom">
                  <i className="fas fa-table mr-2" style={{ color: '#6366f1' }}></i>
                  Desglose por duración <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(alquileres completados)</span>
                </div>
                <div className="card-body-custom" style={{ padding: 0 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={thStyle}>Duración</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Alquileres</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>% del total</th>
                        <th style={{ ...thStyle }}>Barra</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dist.filter(d => d.count > 0).map((d, i) => (
                        <tr
                          key={d.label}
                          style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}
                        >
                          <td style={tdStyle}>
                            <span
                              style={{
                                display: 'inline-block',
                                width: 10,
                                height: 10,
                                borderRadius: 3,
                                background: d.color,
                                marginRight: 8,
                              }}
                            />
                            {d.label} día{d.label === '1' ? '' : 's'}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>
                            {d.count.toLocaleString()}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#6b7280' }}>
                            {d.pct.toFixed(1)}%
                          </td>
                          <td style={{ ...tdStyle, width: '40%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div
                                style={{
                                  height: 10,
                                  width: `${Math.round(d.pct)}%`,
                                  background: d.color,
                                  borderRadius: 4,
                                  minWidth: d.count > 0 ? 4 : 0,
                                  transition: 'width 0.3s ease',
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: '#f0f4ff', fontWeight: 700 }}>
                        <td style={tdStyle}>Total completados</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{completedCount.toLocaleString()}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>100%</td>
                        <td style={tdStyle} />
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Abandoned (CANCELED) chart ──────────────────────────────── */}
            <div className="dashboard-card-section mb-3" style={{ borderLeft: '4px solid #ef4444' }}>
              <div className="card-header-custom" style={{ color: '#ef4444' }}>
                <i className="fas fa-chart-bar mr-2"></i>
                Distribución por duración de alquiler{' '}
                <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(abandonados en carrito)</span>
              </div>
              <div className="card-body-custom">
                {canceledCount === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0', fontSize: 14 }}>
                    <i className="fas fa-inbox" style={{ fontSize: 28, display: 'block', marginBottom: 8 }}></i>
                    No hay alquileres abandonados para el periodo seleccionado.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={distCanceled} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} tickFormatter={v => `${v}d`} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip
                        formatter={(value, name, props) => [
                          `${value} (${props.payload.pct.toFixed(1)}%)`,
                          'Abandonados',
                        ]}
                        labelFormatter={l => `Duración: ${l} día${l === '1' ? '' : 's'}`}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* ── Abandoned breakdown table ────────────────────────────────── */}
            {canceledCount > 0 && (
              <div className="dashboard-card-section mb-3" style={{ borderLeft: '4px solid #ef4444' }}>
                <div className="card-header-custom" style={{ color: '#ef4444' }}>
                  <i className="fas fa-table mr-2"></i>
                  Desglose por duración{' '}
                  <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(abandonados en carrito)</span>
                </div>
                <div className="card-body-custom" style={{ padding: 0 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#fff5f5' }}>
                        <th style={thStyle}>Duración</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Abandonados</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>% del total</th>
                        <th style={{ ...thStyle }}>Barra</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distCanceled.filter(d => d.count > 0).map((d, i) => (
                        <tr
                          key={d.label}
                          style={{ borderBottom: '1px solid #fef2f2', background: i % 2 === 0 ? '#fff' : '#fff8f8' }}
                        >
                          <td style={tdStyle}>
                            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#ef4444', marginRight: 8 }} />
                            {d.label} día{d.label === '1' ? '' : 's'}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{d.count.toLocaleString()}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: '#6b7280' }}>{d.pct.toFixed(1)}%</td>
                          <td style={{ ...tdStyle, width: '40%' }}>
                            <div style={{ height: 10, width: `${Math.round(d.pct)}%`, background: '#ef4444', borderRadius: 4, minWidth: d.count > 0 ? 4 : 0, transition: 'width 0.3s ease' }} />
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: '#fff0f0', fontWeight: 700 }}>
                        <td style={tdStyle}>Total abandonados</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{canceledCount.toLocaleString()}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>100%</td>
                        <td style={tdStyle} />
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

const thStyle = {
  padding: '10px 16px',
  textAlign: 'left',
  fontWeight: 700,
  fontSize: 11,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  borderBottom: '2px solid #e5e7eb',
};

const tdStyle = {
  padding: '9px 16px',
  verticalAlign: 'middle',
};

function mapStateToProps(state) {
  return {
    rentals: state.rentals ? state.rentals.rentals : null,
    error: state.rentals ? state.rentals.error : null,
    customers: state.customers ? state.customers.customers : null,
    role: state.auth.role,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ fetchRentals, fetchCustomersName }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(DashboardRentalView);
